import type * as React from 'react';

import { compose } from 'recompose';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import connectToRouter from 'js/lib/connectToRouter';

import type { CMLContent } from 'bundles/compound-assessments/types/FormParts';
import ModerationDropdown from 'bundles/discussions/components/ModerationDropdown';
import discussionsForumsHOC from 'bundles/discussions/components/discussionsForumsHOC';
import type { AuthorProfile, Contributor } from 'bundles/discussions/lib/types';
import { getAssignmentPostItemLink } from 'bundles/discussions/utils/discussionsUrl';
import routerConnectToCurrentForum from 'bundles/discussions/utils/routerConnectToCurrentForum';

import { extractForumPostId } from '../../../__helpers__/forumPostDataHelpers';
import type { CreatorType, ForumPost, ForumPostLookupProps, ForumQuestionsV1Resource } from '../__types__';

type ForumQuestionsDataProviderResponse = {
  forumPost?: ForumPost & CreatorType;
  contributors?: Contributor[];
  replies?: (ForumPost & CreatorType)[];
};

function extractCreator(response: ForumQuestionsV1Resource, creatorId: number): AuthorProfile | undefined {
  if (!response.ForumQuestionsV1Resource.linked || !response.ForumQuestionsV1Resource.linked.onDemandSocialProfilesV1) {
    return undefined;
  }
  return response.ForumQuestionsV1Resource.linked?.onDemandSocialProfilesV1?.find(
    (contributor: AuthorProfile) => contributor && contributor.userId === creatorId
  );
}

function extractForumPost(response: ForumQuestionsV1Resource): ForumPost & { creator?: AuthorProfile } {
  // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  const forumPost: ForumPost = response.ForumQuestionsV1Resource.elements[0];
  // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  const creatorId = response.ForumQuestionsV1Resource.elements[0].creatorId;
  const deepLink = getAssignmentPostItemLink(forumPost.forumId, forumPost.forumQuestionId);
  const creator: AuthorProfile | undefined = extractCreator(response, creatorId);
  return { ...forumPost, creator, deepLink };
}

const parseTabsApiResponse = (response: ForumQuestionsV1Resource): ForumQuestionsDataProviderResponse => {
  if (!response || (response && !('ForumQuestionsV1Resource' in response))) {
    return {} as ForumQuestionsDataProviderResponse;
  }

  return {
    forumPost: extractForumPost(response),
  } as ForumQuestionsDataProviderResponse;
};

type Question = {
  content: {
    question: CMLContent;
  };
  state: { deleted: boolean };
  questionId: string;
};

type Props = {
  questionId: string;
  question: Question;
  userId: number;
  courseId: string;
  dontNotify?: boolean;
  editReason?: string;
  showEditor: boolean;
};

export const ForumQuestionsProvider: React.FC<ForumPostLookupProps & Props> = (props) => {
  const content: ForumPost = {
    content: { details: props.question?.content?.question },
    courseId: props.courseId,
    createdAt: 0,
    creatorId: 0,
    definition: { dtdId: props.question?.content?.question?.definition?.dtdId },
    id: props.question?.questionId,
    forumQuestionId: props.forumQuestionId,
    showEditor: props.showEditor,
    isFlagged: false,
    isUpvoted: false,
    totalAnswerCount: 0,
    typeName: 'cml',
    upvoteCount: 0,
    userId: 0,
    question: props.question?.content?.question?.definition?.value,
    dontNotify: props.dontNotify,
    editReason: props.editReason,
    questionId: extractForumPostId(props.forumQuestionId),
  };

  const forumQuestion = parseTabsApiResponse({
    ForumQuestionsV1Resource: {
      elements: [content],
      linked: { onDemandCourseForumAnswersV1: [], onDemandSocialProfilesV1: [] },
    },
  });

  return props.children({ loading: false, error: undefined, data: forumQuestion });
};

export default compose<ForumPostLookupProps & Props, ForumPostLookupProps>(
  connectToStores<ForumPostLookupProps & { courseId: string; userId: string }, ForumPostLookupProps>(
    ['ThreadDetailsStore', 'CourseStore', 'ApplicationStore'],
    ({ ThreadDetailsStore, CourseStore, ApplicationStore }, { forumQuestionId }) => {
      const question = ThreadDetailsStore.getQuestion(forumQuestionId);

      return {
        question,
        courseId: CourseStore.getCourseId(),
        authenticated: ApplicationStore.isAuthenticatedUser(),
        isSuperuser: ApplicationStore.isSuperuser(),
      };
    }
  ),
  discussionsForumsHOC({
    fields: ['link', 'title'],
    subcomponents: [ModerationDropdown],
  }),
  connectToRouter(routerConnectToCurrentForum)
)(ForumQuestionsProvider);
